import React, { useEffect, useMemo, useRef, useState } from "react";
import { imageFromUuid } from "../../helpers/utility";
import {
  useGetFoxitSignatureQuery,
  useSaveFoxitSignatureMutation,
  useDeleteFoxitSignatureMutation,
} from "../../services/foxitServices";
import "./foxit.css";
import moment from "moment";
import _ from "lodash"; // Make sure lodash is imported
import { useGroupChatContext } from "../../context/groupchatContext";
import { Loader } from "../loader";
import FullScreenLoader from "../FullScreenLoader";

const PDFViewer = ({ fileUrl, setPdfUiIstance, isPreviewMode = false }) => {
  const groupChatContext = useGroupChatContext();
  const { authData } = useMemo(() => ({
    authData: groupChatContext.authData,
  }));
  window.handleProviderStampAfterSignatureClick = function (
    SignatureCoordinates,
    activeDoc
  ) {
    setTimeout(() => {
      insertProviderNameAfterSignature(SignatureCoordinates, activeDoc);
    }, 10);
  };
  const elementRef = useRef(null);
  const pdfuiRef = useRef(null); // To store the PDFUI instance
  const { data: signatureData } = useGetFoxitSignatureQuery("", {
    skip: !authData?.is_provider,
    refetchOnMountOrArgChange: true
  });
  const [saveFoxitSignature] = useSaveFoxitSignatureMutation();
  const [deleteFoxitSignature] = useDeleteFoxitSignatureMutation();
  const [loading, setLoading] = useState(true);
  let options = {};
  let fragments = null;
  let providerInfoValue = null;
  let providerInfoName = "";
  var isInsertProvidername = false;
  const manualAnnotContent = {
    prefix: "~~manual~annot~content~~",
    separator: "|",
  };
  const customComponents = {
    popupTW: {
      // title: 'Typewriter',
      title: "Comments",
      type: "popup-typewriter",
    },
    datePicker: {
      title: "Date Picker",
      type: "date-picker",
    },
    providerInfo: {
      title: "Paste Provider Info",
      type: "provider-info",
    },
    signaturesDropdown: {
      componentName: "signatures-dropdown",
      title: "Signatures",
    },
    commentTab: {
      title: "Tools",
    },
  };
  const defaultStateName = "hand";
  const freeTextTypewriter = {
    stateName: "createFreeTextTypewriter",
    intent: "FreeTextTypewriter",
    type: "freetext",
  };
  const newUIEvents = {
    addTextObjectParamTransfer: "add-text-object-param-transfer",
    addInksign: "add-inksign",
  };
  const encodedImgPrefix = "data:image/png;base64,";

  const textSizeDiff = 4; // difference of "small" and "large" text sizes from "normal"...
  const annotTextSizes = { default: 12 };
  annotTextSizes.normal = annotTextSizes.default;
  annotTextSizes.small = annotTextSizes.normal - textSizeDiff;
  annotTextSizes.large = annotTextSizes.normal + textSizeDiff;

  // examples:
  //   [255.0, 0.0, 0.0, 255.0] => // blue color
  //   [0.0, 255.0, 0.0, 255.0] => // green color
  //   [0.0, 0.0, 255.0, 255.0] => // red color
  //   [0.0, 0.0, 0.0, 255.0] => // black color
  function convertArrayToABGR(va) {
    let res = 0;
    for (let i = 0; i < va.length; ++i) {
      let color = va[i];
      color <<= 8 * i;
      res += color;
    }
    return res >>> 0;
  }

  function getElementCoords(element) {
    const box = element.getBoundingClientRect(),
      coordX = box.left + (box.right - box.left) / 2,
      coordY = box.top + (box.bottom - box.top) / 2;

    return { X: coordX, Y: coordY };
  }

  function simulateMouseEvent(element, eventName) {
    if (typeof element === "string") {
      element = document.querySelector(element);
    }

    const coords = getElementCoords(element);

    if (!eventName) eventName = "click"; // if eventName not provided, then default is "click"

    element.dispatchEvent(
      new MouseEvent(eventName, {
        view: window,
        bubbles: true,
        cancelable: true,
        clientX: coords.X,
        clientY: coords.Y,
        button: 0,
      })
    );

    return new Promise((resolve) => setTimeout(() => resolve()));
  }

  function splitLines(text) {
    return (text || "").split(/\r\n|\r|\n/);
  }

  function getFragments() {
    if (_.isEmpty(fragments)) {
      // first remove components (or group of components) that aren't needed
      fragments = [
        UIExtension.UIConsts.COMPONENT_NAMES.SNAPSHOT_BUTTON,
        "open-file-button-list",
        "home-tab-group-page",
        "home-tab-group-magnifier",
        "home-tab-group-marquee",
        "dropdown-zoom",
        "comment-tab-group-note",
        "comment-tab-group-mark",
        "freetext-textbox",
        "create-polygon",
        "create-polyline",
        "create-cloud",
        "comment-tab-group-stamp",
        "comment-tab-group-measurement",
        "create-fileattachment",
        "create-link",
        UIExtension.UIConsts.COMPONENT_NAMES.EDIT_TAB,
        UIExtension.UIConsts.COMPONENT_NAMES.PROTECT_TAB,
        UIExtension.UIConsts.COMPONENT_NAMES.BOOKMARK_SIDEBAR_PANEL,
        UIExtension.UIConsts.COMPONENT_NAMES.LAYERS_SIDEBAR_PANEL,
        UIExtension.UIConsts.COMPONENT_NAMES.COMMENT_LIST_SIDEBAR_PANEL,
        "fv--contextmenu-item-reply",
        "fv--contextmenu-item-marquee-zoom",
      ].map((c) => {
        return {
          target: c,
          action: UIExtension.UIConsts.FRAGMENT_ACTION.REMOVE,
        };
      });

      const { renderTo } = options;

      // now add custom UI snippets
      fragments.push(
        {
          target: "freetext-typewriter",
          config: {
            callback: {
              before: function () {
                // console.log('before');
              },
              after: function (value, evt) {
                const dpComponent =
                  pdfuiRef.current.getComponentByName("date-picker-button");
                const piComponent = pdfuiRef.current.getComponentByName(
                  "provider-info-button"
                );

                // if this button actually triggered by human interaction AND
                // either "date-picker-button" OR "provider-info-button" is active, THEN
                // simulate click on itself again...
                if (
                  evt[0] &&
                  evt[0].isTrusted &&
                  (dpComponent.isActive || piComponent.isActive)
                ) {
                  simulateMouseEvent(
                    `${renderTo} a[name="freetext-typewriter"]`
                  );
                }
              },
            },
          },
        },
        {
          target: "freetext-typewriter",
          action: UIExtension.UIConsts.FRAGMENT_ACTION.AFTER,
          template: `<xbutton @tooltip name="popup-typewriter-button" icon-class="fv__icon-toolbar-popup-typewriter">${customComponents.popupTW.title}</xbutton>`,
          config: {
            target: "popup-typewriter-button",
            tooltip: {
              title: customComponents.popupTW.title,
            },
            callback: function () {
              // if "popup-typewriter-button" is already selected, then simply pass control to "hand-tool"
              if (this.component.isActive) {
                return simulateMouseEvent(`${renderTo} a[name="hand-tool"]`);
              }

              openTypewriterModal();
            },
          },
        },
        {
          target: "popup-typewriter-button",
          action: UIExtension.UIConsts.FRAGMENT_ACTION.AFTER,
          template: `<xbutton @tooltip name="date-picker-button" icon-class="fv__icon-toolbar-date-picker">${customComponents.datePicker.title}</xbutton>`,
          config: {
            target: "date-picker-button",
            tooltip: {
              title: customComponents.datePicker.title,
            },
            callback: function () {
              // if "date-picker-button" is already selected, then simply pass control to "hand-tool"
              if (this.component.isActive) {
                return simulateMouseEvent(`${renderTo} a[name="hand-tool"]`);
              }

              openDatePickerModal();
            },
          },
        },
        {
          target: "date-picker-button",
          action: UIExtension.UIConsts.FRAGMENT_ACTION.AFTER,
          template: `<xbutton @tooltip name="provider-info-button" icon-class="fv__icon-toolbar-provider-info">${customComponents.providerInfo.title}</xbutton>`,
          config: {
            target: "provider-info-button",
            tooltip: {
              title: customComponents.providerInfo.title,
            },
            /* callback: class extends UIExtension.Controller {
              mounted() {
                console.log('mounted provider-info-button');
              }
              handle(evt) {
                console.log('"provider-info-button" clicked ' + evt);
              }
            }, */
            callback: function () {
              // if "provider-info-button" is already selected, then simply pass control to "hand-tool"
              if (this.component.isActive) {
                return simulateMouseEvent(`${renderTo} a[name="hand-tool"]`);
              }

              handleProviderInfoClick();
            },
          },
        },
        {
          target: "provider-info-button",
          action: UIExtension.UIConsts.FRAGMENT_ACTION.AFTER,
          template: "<ink-sign-dropdown></ink-sign-dropdown>",
        },
        {
          target: customComponents.signaturesDropdown.componentName,
          config: {
            tooltip: {
              title: customComponents.signaturesDropdown.title,
            },
            text: customComponents.signaturesDropdown.title,
          },
        },
        {
          target: UIExtension.UIConsts.COMPONENT_NAMES.COMMENT_TAB,
          config: {
            title: customComponents.commentTab.title,
          },
        }
        /* ,{
          target: 'comment-tab-group-text',
          action: UIExtension.UIConsts.FRAGMENT_ACTION.APPEND,
          template: `
            <dropdown @tooltip name="rotation-dropdown" icon-class="fv__icon-toolbar-rotation">
              <dropdown-button name="rotate-left-dropdown-button">Rotate Left</dropdown-button>
              <dropdown-button name="rotate-right-dropdown-button">Rotate Right</dropdown-button>
            </dropdown>
          `,
          config: [
            {
              target: 'rotation-dropdown',
              tooltip: {
                title: 'Rotate Document',
              },
            },
            {
              target: 'rotate-left-dropdown-button',
              callback: function() { return rotateDoc('left'); },
            },
            {
              target: 'rotate-right-dropdown-button',
              callback: function() { return rotateDoc('right'); },
            },
          ],
        } */
      );
    }

    return fragments;
  }

  function getAddons() {
    const addonsPath = `/lib/uix-addons`;
    return [`${addonsPath}/print`];
    /* return [
      `${addonsPath}/file-property`,
      `${addonsPath}/multi-media`,
      `${addonsPath}/password-protect`,
      `${addonsPath}/redaction`,
      `${addonsPath}/path-objects`,
      `${addonsPath}/print`,
      `${addonsPath}/full-screen`,
      `${addonsPath}/import-form`,
      `${addonsPath}/export-form`,
      // `${addonsPath}/undo-redo`,
      `${addonsPath}/text-object`,
    ]; */
  }

  function getAnnotContent(annot) {
    return (annot.getContent() || "").trim();
  }

  function handleProviderInfoClick() {
    if (!pdfui) return;

    const { renderTo } = options;

    const promises = [
      pdfui.current.getPDFViewer(),
      pdfui.current.getComponentByName("provider-info-button"),
      pdfui.current.getComponentByName("freetext-typewriter"),
    ];

    Promise.all(promises).then((values) => {
      const viewer = values[0];
      const piComponent = values[1];
      const twComponent = values[2];

      const currState = viewer.stateHandlerManager
        .getCurrentStates()
        .getStateName();
      let promise = Promise.resolve();

      if (currState === freeTextTypewriter.stateName) {
        promise = simulateMouseEvent(
          `${renderTo} a[name="freetext-typewriter"]`
        );
      }

      promise.then(() => {
        simulateMouseEvent(`${renderTo} a[name="freetext-typewriter"]`).then(
          () => {
            twComponent.deactive();
            piComponent.active();
          }
        );
      });
    });
  }

  useEffect(() => {
    if (pdfuiRef.current && fileUrl) {
      let url = imageFromUuid(fileUrl);
      setLoading(true)
      fetch(url).then(function (response) {
        response.arrayBuffer().then(function (buffer) {
          pdfuiRef.current.openPDFByFile(buffer);
          setLoading(false)
        });
      });
    }
  }, [pdfuiRef.current, fileUrl]);

  useEffect(() => {
    if (window.UIExtension) {
      const element = elementRef.current;
      const libPath = "/lib/";

      pdfuiRef.current = new window.UIExtension.PDFUI({
        viewerOptions: {
          libPath,
          jr: {
            licenseSN:
              "hUVuzlvzrAvtYRvzLCORw2+5Iy873z7YlLXOEdqQibBEcSPoz91umA==",
            licenseKey:
              "PjwcmTawMSysb7uJGyG8Al+PlhAEkpdTk+VkGKFsjqe5rsbZ1SoWBWFuQ0qZUOLydAIcXlccljHNvaSxVWc/k0kh87Kp4BDNFBqX7X+URtXfx6DKDeEKX26kDQ8V6pnSwxZiVmtDo5A2XcgjEVHdqePd3IO1aZtaFK9QLAnOgi7Ni2cM6WIJoohdh/Q7nn9UNH6cUfOHvzdd9vCsueB/sdEgsZrfKJkuP49MTLlC+bQrp7I0Vce2ChxtoSH4XwTKA/gLT3z4dPpWiW/PGTvQibodPEfPl+8s1gx10AkzR9vbVCki9OeVOlsY0sq6UJuHnhzaRJmhByLnVXeDqIFO1fdQvmpBiFfdDEkAQW/nG5nezs9mOaEjhy0bgdtjUil3RNZtJzXbbJ9Z0fBAf3epATZI8r0N7jn8wg8wzCBkiFfLEMrtZQU4dgwHLTdD7W32CClXB/r8cTIEPVjYliD4RTXPPSyvHnzrm3lsXsuL78h+/hqeOPnnqGnAQLOA+QF0a/i21H2FJ8UAp0Qa2JZaJxB+Pc6TDuaacSG0a7VW+WoBSeanS3shfrbDZIeuMjTvy4w3QGwmPAsbNaRfXXgO4CGc1n/+rQe6iK2N3tlYyfyG1nk2rpxcaEW9Ls2EV6HPbh1gja/R0MjZN7TfP8r6EOpeXDtx7Nqx/zAx8TqPMoSnlFehD57UvEqAR5T97zkOLLBz1yGTQXraN07dML6GYRDFZstzI41G+vnBQpBBsEIJ7DUqeFz/0MKpFBgOktxH9HFsxroya+1re1W9/4q/STkq8e4LyohBq347Beb0RnvJTzgyqC7bG1AIx8T9sC4xCKdSpRCZ06TRBQ8bnU4DTj6yhaBruwtLRF9EXvygLb9Q2XwedpHTiWqS9iZvo+DiCFb8uxAt+lgo+pF2pk1oeqowMMDzrnsW1ZY4fc35EGPo+PuCaM7r60AdWhGOiEts2KtmVdwXEw8+sSytZgpwKBeo8tbtx0f7S+f1n0ezEVsbU/FEoOEu9/ox192H2vF3UV8xNcr0DGbtVz9ewKbn3QHiXAVRdQe+ri0EP+Bj3nM37ZPOtKsoSEmeddThsjHQ+xsw8FwUpN/9FxGMpth+b6OkzaVutpIaiHBX9AczbMQLrCZ+2zH3a6oFJJHfr5teCw8Hse2GxHTHNv+PlKJy2Brxgpb8GuM6Eesmd2uLgm0ss0AvQDRwAq+lCdQ=",
            readyWorker: window.readyWorker,
          },
        },
        renderTo: element,
        //   addons: UIExtension.PDFViewCtrl.DeviceInfo.isMobile
        //     ? '/lib/uix-addons/allInOne.mobile.js'
        //     : '/lib/uix-addons/allInOne.js',
        // addons: UIExtension.PDFViewCtrl.DeviceInfo.isMobile ? mobileAddons : Addons,
        fragments: getFragments(),
        addons: getAddons(),
      });
      // Set up ink signature event handler
      pdfuiRef.current.getPDFViewer().then((viewer) => {
        const encodedImgPrefix = "data:image/png;base64,";

        viewer.eventEmitter.on(UIExtension.UIEvents.annotationAdd, (annots) => {
          pdfuiRef.current.isModified = true;

          const annot = annots[0] || null;

          // if there is no annotation (somehow), then do nothing...
          if (!annot) return;

          // if annot is not a "freeTextTypewriter", then do nothing...
          if (annot.info.type !== freeTextTypewriter.type) return;

          // change default text size and color...
          const newAppearance = Object.assign({}, annot.getDefaultAp(), {
            textColor: convertArrayToABGR([0.0, 0.0, 0.0, 255.0]),
            textSize: annotTextSizes.default,
          });

          const cContent = getAnnotContent(annot);
          let popupTWComponent = pdfuiRef.current.getComponentByName(
            "popup-typewriter-button"
          );
          let dpComponent =
            pdfuiRef.current.getComponentByName("date-picker-button");
          let piComponent = pdfuiRef.current.getComponentByName(
            "provider-info-button"
          );
          if (
            (popupTWComponent.isActive ||
              _.startsWith(cContent, manualAnnotContent.prefix)) &&
            popupTypewriter.textSize &&
            _.has(annotTextSizes, popupTypewriter.textSize)
          ) {
            newAppearance.textSize = annotTextSizes[popupTypewriter.textSize];
          }

          const annotationAddPromises = [];
          annotationAddPromises.push(annot.setDefaultAp(newAppearance));

          if (_.startsWith(cContent, manualAnnotContent.prefix)) {
            const actualContent = cContent.substring(
              cContent.indexOf(manualAnnotContent.separator) + 1
            );
            annotationAddPromises.push(annot.setContent(actualContent));
            return Promise.all(annotationAddPromises).then(() => {
              annot.customType = customComponents.popupTW.type;

              return fixAnnotVisibility(annot).then(() => {
                return fixExtraAnnotPosition(annot)
                  .then(_.noop)
                  .catch(_.noop)
                  .then(() => addExtraAnnot(annot));
              });
            });
          }

          // if "popup-typewriter-button" AND "date-picker-button" AND "provider-info-button" aren't active
          // then do nothing...
          if (
            !popupTWComponent.isActive &&
            !dpComponent.isActive &&
            !piComponent.isActive &&
            !isInsertProvidername
          )
            return;

          // set new contents and customType
          let newContent = null;
          let customType = null;
          if (popupTWComponent.isActive) {
            newContent = popupTypewriter.value;
            customType = customComponents.popupTW.type;
          } else if (dpComponent.isActive) {
            newContent = moment(dateObj.value || new Date()).format(
              dateObj.format
            );
            customType = customComponents.datePicker.type;
          } else if (piComponent.isActive) {
            newContent = providerInfoValue;
            customType = customComponents.providerInfo.type; // saqib
          } else if (isInsertProvidername) {
            // newContent = "Electronically signed by Saqib Mahmood111";  // Temp - Saqib Mahmood
            newContent =
              "Electronically signed by " +
              providerInfoName +
              " on " +
              moment().format("MM/DD/YYYY hh:mm A");

            const practiceId = parent.getSelectedPracticeId();
            // console.log('practiceId: ', practiceId);
            // For Oakmed Practice, Clear the "Electronically signed by " Text.
            if (practiceId == 320 || practiceId == 183) {
              newContent = "";
            }

            // For enrollment Flow, exclude the provider name.
            var pseFlow = localStorage.getItem("extFormsPatientSelfEnrollment")
              ? true
              : false;
            if (pseFlow) {
              newContent =
                "Electronically signed on " +
                moment().format("MM/DD/YYYY hh:mm A");
            }

            customType = customComponents.providerInfo.type; // saqib
          }

          // if no new contents available to be set, then do nothing...
          if (!newContent) return;

          previousAnnot = null;
          extraAnnots = [];

          if (popupTWComponent.isActive && !popupTypewriter.combineText) {
            const result = createExtraAnnots(annot, newContent);
            newContent = result.mainContent;
            extraAnnots = result.extraAnnots;
          }

          setTimeout(() => {
            // debugger
            viewer.getStateHandlerManager().switchTo(defaultStateName);

            annotationAddPromises.push(annot.setContent(newContent));

            Promise.all(annotationAddPromises).then(() => {
              if (!customType) {
                return;
              }

              annot.customType = customType;
              isInsertProvidername = false;

              return fixAnnotVisibility(annot).then(() => {
                if (customType === customComponents.popupTW.type) {
                  addExtraAnnot(annot);
                }
              });
            });
          });
        });

        viewer.eventEmitter.on(window.UIExtension.UIEvents.inkSignAdded, () => {
          if (isPreviewMode) return;

          const signWithoutDbIdIndex = _.findIndex(
            viewer.inkSignManager.inkSignList,
            (sign) => !sign.dbId
          );

          if (signWithoutDbIdIndex === -1) return;

          // Get the current signature that was just added
          const currSign =
            viewer.inkSignManager.inkSignList[signWithoutDbIdIndex];
          const dataUrl = currSign.dataURL || currSign.getDataURL();

          // Check wet signature status using plain JavaScript
          const wetCheckbox = document.querySelector(
            "input#sign-dialog-wet-checkbox"
          );
          let is_wet = 0;
          if (wetCheckbox && wetCheckbox.checked) {
            is_wet = 1;
          }

          // Prepare the parameters for the API call
          const params = {
            image_data: dataUrl.replace(encodedImgPrefix, ""),
            device: "web",
            is_default: "1",
            is_wet: is_wet,
          };

          console.log("Add Signature Params:", params);

          // Use RTK Query mutation to save the signature
          if (authData?.is_provider) {
            saveFoxitSignature(params)
              .unwrap()
            .then((res) => {
              // Update the signature with the returned ID
              console.log(res.data, "res.data");
              viewer.inkSignManager.inkSignList[signWithoutDbIdIndex].dbId =
                res.data.id;
              viewer.inkSignManager.setIsDefaultByIndex(signWithoutDbIdIndex);
              viewer.inkSignManager.setCurrentInkSign(signWithoutDbIdIndex);

              // Refresh the signature list
              setTimeout(() => {
                viewer.inkSignManager.reRenderSignList();
                // Call parent reload function if it exists
                if (window.parent.reloadEFaxSignatures) {
                  window.parent.reloadEFaxSignatures();
                }
              });
            })
            .catch((err) => {
              console.error("Error saving signature:", err);
              viewer.inkSignManager.delete(signWithoutDbIdIndex);
            });
          }
        });

        // Signature Before Delete Event Handler
        viewer.eventEmitter.on(
          window.UIExtension.UIEvents.inkSignBeforeDeleted,
          (deleteIndex) => {
            // Remove active class from signature dropdown
            const signDropdown = document.querySelector(
              ".fv__ui-sign-dropdown"
            );
            if (signDropdown) {
              signDropdown.classList.remove("active");
            }

            if (isPreviewMode) return;

            const currentInkSign = viewer.inkSignManager.currentInkSign;
            const deleteInkSign =
              viewer.inkSignManager.inkSignList[deleteIndex];

            if (!deleteInkSign || !deleteInkSign.dbId) {
              return;
            }

            const wasDefaultSignDeleted =
              currentInkSign && currentInkSign.dbId === deleteInkSign.dbId;

            // Use RTK Query mutation to delete the signature
            if (authData?.is_provider) {
              deleteFoxitSignature({ id: deleteInkSign.dbId })
                .unwrap()
              .then((response) => {
                if (response.code !== 0) {
                  throw new Error(response.msg);
                }

                if (wasDefaultSignDeleted) {
                  loadSignatures(); // Using the existing loadSignatures function
                } else if (window.parent.reloadEFaxSignatures) {
                  window.parent.reloadEFaxSignatures();
                }
              })
              .catch((err) => {
                console.error("Error deleting signature:", err);
                  loadSignatures(); // Reload signatures on error
                });
            }
          }
        );
      });

      if (setPdfUiIstance) {
        setPdfUiIstance(pdfuiRef.current);
      }
    }
    // Cleanup on component unmount
    return () => {
      if (pdfuiRef.current) {
        pdfuiRef.current.destroy();
      }
    };
  }, []);

  const loadSignatures = () => {
    pdfuiRef.current.getPDFViewer().then((viewer) => {
      viewer.inkSignManager.reset();

      let encodedImgPrefix = "data:image/png;base64,";

      if (!signatureData?.data?.length) {
        return resolve();
      }

      let currSignIndex = null;
      let isDefaultSetOnlyOnce = 0;
      _.forEach(signatureData?.data, (sign, idx) => {
        let dataUrl = sign.image_data;
        if (!_.startsWith(dataUrl, encodedImgPrefix)) {
          dataUrl = `${encodedImgPrefix}${dataUrl}`;
        }

        // Saqib, Set isdefault only once. So, the default sign will be either Wet or normal.
        // On Mobile they set the default for Wet Sign and also set the Default for Normal Signs. They have 2 default Signs.
        let isDefault = 0;
        if (isDefaultSetOnlyOnce == 0) {
          isDefault = parseInt(sign.is_default) === 1;
          isDefaultSetOnlyOnce = isDefault;
        }
        // console.log('isDefault: ', isDefault);

        // const isDefault = parseInt(sign.is_default) === 1;
        const isWet = sign.is_wet;

        const addSign = viewer.inkSignManager.addInkSign();
        addSign.dbId = sign.id;
        addSign.isDefault = isDefault;
        addSign.isWet = isWet;
        addSign.setDataURL(dataUrl);
        viewer.inkSignManager.push(addSign);

        if (isDefault) {
          currSignIndex = idx;
        }
      });

      if (currSignIndex === null) {
        currSignIndex = 0;
      }

      viewer.inkSignManager.setCurrentInkSign(currSignIndex);

      setTimeout(() => {
        viewer.inkSignManager.reRenderSignList();
      });
    });
  };
  useEffect(() => {
    if (signatureData?.data?.length && pdfuiRef.current) {
      loadSignatures();
    }
  }, [signatureData]);

  const insertProviderNameAfterSignature = (signRect, activeDoc) => {
    pdfuiRef.current.getPDFViewer().then((values) => {
      const viewer = values;
      let promise = Promise.resolve();

      promise.then(() => {
        // var pdfDoc = viewer.getCurrentPDFDoc();
        // console.log('pdfDoc: ', pdfDoc);
        pdfuiRef.current.isModified = true;
        var currentPage = activeDoc.page;
        // console.log('currentPage: ', currentPage);

        var currPageRotation = currentPage.info.rotation;
        // var newContent = "Electronically signed by Saqib Mahmood2";
        // var newContent = "Electronically signed by " + providerInfoName + " on " + moment().format('MM/DD/YYYY hh:mm A');

        // const practiceId = parent.getSelectedPracticeId();

        //mustafa comment practice

        // if(practiceId == 320 || practiceId == 183){
        //   newContent = '';
        // }

        // For enrollment Flow, exclude the provider name.
        // var pseFlow = localStorage.getItem('extFormsPatientSelfEnrollment') ? true : false;
        let newContent = "";
        let pseFlow = true;
        if (pseFlow) {
          newContent =
            "Electronically signed by " +
            JSON.parse(localStorage.getItem("auth"))?.name +
            " on " +
            moment().format("MM/DD/YYYY hh:mm A");
        }

        // debugger
        // const providerSignContents = `${manualAnnotContent.prefix}${manualAnnotContent.separator}${newContent}`;

        // To adjust the Annot not showing on Signature size is reduced.
        var annotTopCord = signRect.top - 40;
        var diff = signRect.top - signRect.bottom;
        if (diff <= 40) {
          annotTopCord = signRect.top - (diff - 2);
        }
        var providerNameAnnot = {
          contents: newContent, // providerSignContents,
          type: "freetext",
          intent: "FreeTextTypewriter",
          subject: "FreeTextTypewriter",
          rect: {
            left: signRect.left,
            right: signRect.right,
            // top: signRect.top - 40,
            top: annotTopCord,
            bottom: signRect.bottom,
            // top: parseFloat(signRect.bottom) - 20,
            // bottom: signRect.top - (parseFloat(signRect.top) - parseFloat(signRect.bottom))
          },
          page: currentPage.info.index,
          rotate: currPageRotation, // e.getRotation(),
          flags: 4,
        };

        // Add the annotation to the page
        viewer.currentPDFDoc
          .addAnnot(currentPage.info.index, providerNameAnnot)
          .then((success) => {
            console.log("success: ", success[0].getInnerRect());
            success[0].setInnerRect({...success[0].getInnerRect(), width: success[0].getInnerRect().width + 200});
            success[0].setContent(newContent);
            console.log("Annot Success: ", success);
          })
          .catch((error) => {
            console.log("Annot Error: ", error);
          });

        // // Adding Annot via Page

        // viewer.currentPDFDoc.getPageByIndex(0).then(page => {
        //   console.log('internal Page: ', page);
        //   var currPageRotation = page.info.rotation;
        //   // var newContent = "Electronically signed by Saqib Mahmood";
        //   var newContent = "Electronically signed by " + providerInfoName + " on " + moment().format('MM/DD/YYYY hh:mm A');
        //   const providerSignContents = `${manualAnnotContent.prefix}${manualAnnotContent.separator}${newContent}`;
        //   var providerNameAnnot = {
        //     contents:   providerSignContents,
        //     type: "freetext",
        //     intent: "FreeTextTypewriter",
        //     subject: "FreeTextTypewriter",
        //     rect: {
        //       left: signRect.left,
        //       right: signRect.right,
        //       top: signRect.top - 40,
        //       bottom: signRect.bottom
        //       // top: parseFloat(signRect.bottom) - 20,
        //       // bottom: signRect.top - (parseFloat(signRect.top) - parseFloat(signRect.bottom))
        //     },
        //     page: 0,
        //     rotate: currPageRotation,  // e.getRotation(),
        //     flags: 4
        //   }
        //   console.log('providerNameAnnot: ', providerNameAnnot);
        //   page.addAnnot(providerNameAnnot).then((success) => {
        //       console.log(success);
        //   })
        //   .catch(error => {
        //       console.log(error);
        //   });
        // });
      });
    });
  };
  //Empty dependency array to run effect only once on mount

  return <> {loading && <Loader/>} <div id="pdf-ui" className="h-[100%]" ref={elementRef} /></>;
};

export default PDFViewer;
